import { collection, doc, getDocs, onSnapshot, setDoc, deleteDoc } from 'firebase/firestore'
import type { Table } from '../../types'
import { dispatchCatalogEvent, TABLES_UPDATED_EVENT } from '../catalogEvents'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'
import { stripUndefined } from './stripUndefined'
import { commitWriteOps, type BatchWriteOp } from './writeBatches'

const tablesCacheByPlace: Record<string, Table[]> = {}
let tablesUnsubscribe: (() => void) | null = null

function tablesRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'tables')
}

function tableDoc(placeId: string, tableId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'tables', tableId)
}

function dispatchTablesUpdated(): void {
  dispatchCatalogEvent(TABLES_UPDATED_EVENT)
}

export async function primeTablesCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  if (tablesUnsubscribe) {
    tablesUnsubscribe()
    tablesUnsubscribe = null
  }
  const snap = await getDocs(tablesRef(placeId))
  tablesCacheByPlace[placeId] = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Table))
  tablesUnsubscribe = onSnapshot(tablesRef(placeId), (snapshot) => {
    tablesCacheByPlace[placeId] = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Table))
    dispatchTablesUpdated()
  })
}

export function getTables(): Table[] {
  const placeId = getCurrentPlaceId()
  return tablesCacheByPlace[placeId] ?? []
}

export function saveTables(tables: Table[]): void {
  const placeId = getCurrentPlaceId()
  tablesCacheByPlace[placeId] = tables
  dispatchTablesUpdated()
  ;(async () => {
    const snap = await getDocs(tablesRef(placeId))
    const ids = new Set(tables.map((t) => t.id))
    const ops: BatchWriteOp[] = []
    for (const d of snap.docs) {
      if (!ids.has(d.id)) ops.push({ type: 'delete', ref: d.ref })
    }
    for (const t of tables) {
      ops.push({
        type: 'set',
        ref: tableDoc(placeId, t.id),
        data: stripUndefined(t) as object,
      })
    }
    await commitWriteOps(ops)
  })().catch((e) => console.error('Firestore saveTables failed', e))
}

function generateTableId(): string {
  return `T-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function addTable(table: Omit<Table, 'id'>): Table {
  const placeId = getCurrentPlaceId()
  const id = generateTableId()
  const full: Table = { ...table, id }
  const cache = tablesCacheByPlace[placeId] ?? []
  tablesCacheByPlace[placeId] = [...cache, full]
  dispatchTablesUpdated()
  setDoc(tableDoc(placeId, id), stripUndefined(full)).catch((e) =>
    console.error('Firestore addTable failed', e),
  )
  return full
}

export function updateTable(id: string, patch: Partial<Table>): Table[] {
  const placeId = getCurrentPlaceId()
  const cache = tablesCacheByPlace[placeId] ?? []
  tablesCacheByPlace[placeId] = cache.map((t) => (t.id === id ? { ...t, ...patch } : t))
  const found = tablesCacheByPlace[placeId].find((t) => t.id === id)
  if (found) {
    setDoc(tableDoc(placeId, id), stripUndefined(found)).catch((e) =>
      console.error('Firestore updateTable failed', e),
    )
  }
  dispatchTablesUpdated()
  return tablesCacheByPlace[placeId]
}

export function deleteTable(id: string): Table[] {
  const placeId = getCurrentPlaceId()
  const cache = tablesCacheByPlace[placeId] ?? []
  tablesCacheByPlace[placeId] = cache.filter((t) => t.id !== id)
  deleteDoc(tableDoc(placeId, id)).catch((e) =>
    console.error('Firestore deleteTable failed', e),
  )
  dispatchTablesUpdated()
  return tablesCacheByPlace[placeId]
}

export function isTablesPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in tablesCacheByPlace
}

/** Clear in-memory cache and unsubscribe from real-time listener. */
export function clearTablesCache(): void {
  if (tablesUnsubscribe) {
    tablesUnsubscribe()
    tablesUnsubscribe = null
  }
  Object.keys(tablesCacheByPlace).forEach((k) => delete tablesCacheByPlace[k])
}
